import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy } from 'lucide-react';
import moment from "moment";
import Barcode from "react-barcode";
import { Cut, Printer } from "react-thermal-printer";
import { toast } from "react-toastify";
import { SocketState } from "../context/SocketProvider";
import { AuthState } from "../context/AuthProvider";
import ModalContainer from "../2d-game/modals/ModalContainer";

function PrintPagePana() {
  const navigate = useNavigate();
  const { socket } = SocketState();
  const { userDetail } = AuthState();

  const [lotteryList, setLotteryList] = useState([]);
  const [previewTicket, setPreviewTicket] = useState(null);
  const [printTicket, setPrintTicket] = useState(null);
  const [copiedTicket, setCopiedTicket] = useState(null);

  // socket responses
  useEffect(() => {
    if (!socket) return;
    const handleResponse = (response) => {
      if (response?.en === "PLAY_MATKA_GET_BET_LISTS" && !response.err) {
        setLotteryList(response.data?.list || []);
      }
      if (response?.en === "GET_ADVANCE_BETS_LISTS") {
        setPreviewTicket(response.data);
      }
      if (response?.en === "PLAY_MATKA_PRINT_TICKETS") {
        if (response.err) {
          toast.error(response.msg || "Print failed");
          return;
        }
        setPrintTicket(response.data?.ticket_info);
      }
    };
    socket.on("res", handleResponse);
    return () => socket.off("res", handleResponse);
  }, [socket]);

  // fetch the bet list on mount
  useEffect(() => {
    if (!socket) return;
    socket.emit("req", { en: "PLAY_MATKA_GET_BET_LISTS", data: {} });
  }, [socket]);

  const handleViewTicket = (ticket_id) => {
    socket.emit("req", { en: "GET_ADVANCE_BETS_LISTS", data: { ticket_id } });
  };

  const handleConfirmPrint = () => {
    if (previewTicket) {
      socket.emit("req", {
        en: "PLAY_MATKA_PRINT_TICKETS",
        data: { ticket_id: previewTicket.ticket_id },
      });
      setPreviewTicket(null);
    }
  };

  const handleCopy = async (ticket_id) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(ticket_id);
        setCopiedTicket(ticket_id);
        setTimeout(() => setCopiedTicket(null), 1000);
      } catch (err) {
        toast.error('Failed to copy ticket ID');
      }
    } else {
      const tempInput = document.createElement('input');
      tempInput.value = ticket_id;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      setCopiedTicket(ticket_id);
      setTimeout(() => setCopiedTicket(null), 1000);
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const renderStatus = (status) => {
    if (status === 0) return <span className="p-3 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">Not Result Declare</span>;
    if (status === 1) return <span className="p-3 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">Cancelled</span>;
    if (status === 2) return <span className="p-3 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">Not Claim</span>;
    if (status === 3) return <span className="p-3 rounded-full text-sm font-semibold bg-green-100 text-green-800">Claimed</span>;
    if (status === 4) return <span className="p-3 rounded-full text-sm font-semibold bg-red-100 text-red-800">Loss</span>;
    return <span className="p-3 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">Unknown</span>;
  };

  return (
    <>
      <ModalContainer title={'RePrint'} navigation={'/pana-game'} style={{ zIndex: 1056 }}>
        <div>
          {/* No Data */}
          {!lotteryList.length && (
            <div className='flex w-[90%] mx-auto flex-wrap h-[calc(100vh-20vh)] overflow-y-auto'>
              <div className='flex justify-center w-full pt-10'>
                <p className='text-center font-bold text-[18px]'>No Data Found</p>
              </div>
            </div>
          )}

          {/* List */}
          {lotteryList.length > 0 && (
            <div className='mt-5'>
              {/* Header */}
              <div className='w-full border-b rounded border-gray-200 py-3.5 mb-2 bg-[#bdbaba] text-black flex px-3'>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Date</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Time</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Ticket ID</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Status</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Win Amount</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'>Bet Amount</div>
                <div className='w-1/6 text-center font-semibold text-[17px]'></div>
              </div>

              {/* Body */}
              <div className='h-[calc(100vh-32vh)] overflow-y-auto'>
                {lotteryList.map((Lottery, index) => (
                  <div
                    key={index}
                    className='w-full border-b border-gray-200 py-3.5 mb-2 bg-white flex px-3'
                  >
                    <div className='w-1/6 text-center font-semibold text-[16px]'>{Lottery.deal_date}</div>
                    <div className='w-1/6 text-center font-semibold text-[16px]'>{Lottery.deal_time}</div>

                    {/* Ticket ID + Copy */}
                    <div className='w-1/6 text-center font-semibold text-[16px] flex items-center justify-center gap-2'>
                      <span>{Lottery.ticket_id}</span>
                      <button
                        onClick={() => handleCopy(Lottery.ticket_id)}
                        className='text-gray-500 hover:text-blue-600 transition-all'
                        title='Copy Ticket ID'
                      >
                        <Copy size={18} />
                      </button>
                      {copiedTicket === Lottery.ticket_id && (
                        <span className='text-green-500 text-sm animate-pulse'>Copied!</span>
                      )}
                    </div>

                    <div className='w-1/6 text-center font-semibold text-[15px] flex justify-center'>
                      {renderStatus(Lottery.status)}
                    </div>
                    <div className='w-1/6 text-center font-semibold text-[15px] flex justify-center'>
                      {Math.floor(Lottery.total_win_amount)}
                    </div>
                    <div className='w-1/6 text-center font-semibold text-[16px]'>
                      {Math.floor(Lottery.total_bet_amount)}
                    </div>

                    <div className='w-1/6 text-center font-semibold text-[16px]'>
                      <button
                        className="bg-[#e54859] hover:bg-[#f03548] text-white font-bold py-2 px-4 rounded w-2/3"
                        type="button"
                        onClick={() => handleViewTicket(Lottery.ticket_id)}
                      >
                        {userDetail?.print ? "RePrint" : "View"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ModalContainer>

      {/* Popup Preview Modal */}
      {previewTicket && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50" style={{ zIndex: 1111 }}>
          <div className="bg-white w-[320px] rounded shadow-lg p-4 text-[12px] font-mono">
            <h2 className=" font-bold">PANA</h2>
            <p className="italic text-sm mb-4">Amusement Only, Pana Game</p>

            <div className="text-[12px] space-y-1 font-semibold">
              <div>Game Date: {previewTicket.deal_date}</div>
              <div>Game Time: {previewTicket.deal_time}</div>
              <div>Game ID: {previewTicket.ticket_id}</div>
              <div>Agent ID: {userDetail?.user_name}</div>
            </div>

            <div className="mt-2 text-[12px] font-bold">
              <div>Total Selected Spots: {Object.keys(previewTicket.card_details || {}).length}</div>
              <div>Total Point: {Math.floor(previewTicket.total_bet_amount)}</div>
            </div>

            <div className="mt-2 text-[12px]">
              {Object.entries(previewTicket.card_details || {}).map(([category, numbers]) => (
                <div key={category} className="mb-3">
                  <div className="font-bold uppercase mb-1">{category}:</div>
                  <div className="grid grid-cols-3 text-[12px]">
                    {Object.entries(numbers).map(([num, amt]) => (
                      <div key={num}>{num} - {amt}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center font-bold mt-4 text-lg">{previewTicket.ticket_id}</div>

            {previewTicket?.is_advance && (
              <table className="w-full leading-none bordered font-mono">
                <tbody>
                  {previewTicket.bets?.map((bet, index) => (
                    <tr key={index}>
                      <td className=" text-[12px] border px-1 py-1 text-center">{bet.deal_time}</td>
                      <td className="text-[12px] border px-1 py-1 text-center">{bet.ticket_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="flex gap-2 justify-center mt-3">
              <button onClick={() => setPreviewTicket(null)} className="bg-gray-500 text-white px-3 py-1 rounded">
                Close
              </button>
              {userDetail?.print ? (
                <button onClick={handleConfirmPrint} className="bg-red-500 text-white px-3 py-1 rounded">
                  Print
                </button>
              ) : ''}
            </div>
          </div>
        </div>
      )}

      <div id="rePrintContainerPana">
        <Content printTicket={printTicket} onPrinted={() => setPrintTicket(null)} />
      </div>
    </>
  );
}

const LABELS = { single: 'SINGLE', jodi: 'JODI', single_pana: 'SP', double_pana: 'DP', triple_pana: 'TP' };

function Content({ printTicket, onPrinted }) {
  const [printObj, setPrintObj] = useState({});

  // group card_details by category -> [{ number, value }]
  const groupedBets = useMemo(() => {
    const groups = {};
    const cd = printTicket?.card_details || {};
    Object.entries(cd).forEach(([type, numbers]) => {
      groups[type] = Object.entries(numbers).map(([number, value]) => ({ number, value }));
    });
    return groups;
  }, [printTicket]);

  const qty = useMemo(() => {
    const cd = printTicket?.card_details || {};
    return Object.values(cd).reduce((sum, numbers) => sum + Object.keys(numbers).length, 0);
  }, [printTicket]);

  useEffect(() => {
    setPrintObj(printTicket?.card_details ? { ...printTicket.card_details } : {});
  }, [printTicket]);

  useEffect(() => {
    if (Object.keys(printObj).length > 0) {
      const printContainer = document.getElementById("rePrintContainerPana").innerHTML;
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(`<html>
          <head>
            <title>Pana</title>
            <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400&display=swap" rel="stylesheet">
          </head>
          <style>
            @media print { html, body { width: 62mm; height: auto; position: absolute; } }
            body { font-family: 'Roboto', sans-serif; }
            #invoice-POS { padding: 0px; width: 62mm; background: none; margin-top: 0mm !important; }
          </style>
          <body style="margin:0px 8px">`);
        printWindow.document.write(printContainer);
        printWindow.document.write("</body></html>");
        printWindow.document.close();
        printWindow.focus();
        setTimeout(function () {
          printWindow.print();
          printWindow.close();
          if (onPrinted) onPrinted();
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printObj]);

  if (!Object.keys(printObj).length) {
    return null;
  }

  return (
    <>
      <Printer type="epson" characterSet="korea">
        <div className="printArea" style={{ width: "58mm", fontFamily: "Arial, sans-serif", padding: "2px", fontSize: "14px" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "4px" }}>
            <h2 style={{ fontSize: "14px", margin: "2px 0", fontWeight: "bold" }}>Golden Lotto Pana</h2>
            <p style={{ margin: "2px 0", fontStyle: "italic" }}>(Ticket valid for 10 Days)</p>
            {localStorage.getItem('is_gst') === "true" ? `GST No : ${localStorage.getItem('gst')}` : ""}
            <p style={{ margin: "2px 0" }}>Amusement Only</p>
            <p style={{ margin: "2px 0" }}>Reprint Ticket</p>
          </div>

          <hr style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />

          {/* Ticket Info */}
          <div style={{ marginBottom: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Draw:</span>
              <span>{printTicket?.deal_date} {printTicket?.deal_time}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Desk:</span>
              <span>{printTicket?.user_name}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Coupen Time:</span>
              <span>{moment(printTicket?.createdAt).utcOffset("+05:30").format("DD-MM-YYYY hh:mm:ss A")}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
              <span>Qty: {qty}</span>
              <span>Point: {printTicket?.total_bet_amount}</span>
            </div>
          </div>

          <hr style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />

          {/* Bets */}
          <div style={{ marginBottom: "6px" }}>
            {Object.entries(groupedBets).map(([type, bets]) => {
              const chunks = [];
              for (let i = 0; i < bets.length; i += 3) chunks.push(bets.slice(i, i + 3));
              return (
                <div key={type} style={{ width: "58mm", maxWidth: "58mm", display: "flex", flexDirection: "column" }}>
                  <div style={{ fontWeight: "bold", textTransform: "capitalize", fontSize: "14px" }}>
                    {LABELS[type] || type}:
                  </div>
                  {chunks.map((chunk, idx) => (
                    <div key={idx} style={{ fontWeight: "bold", fontSize: "14px", whiteSpace: "pre-wrap" }}>
                      {chunk.map((bet, i) => (
                        <span key={i} style={{ marginRight: "10px" }}>
                          {bet.number}<span style={{ fontWeight: "bold", fontSize: "14px" }}>*</span>{bet.value}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <hr style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />

          {/* Barcode */}
          <div style={{ textAlign: "center", marginTop: "4px" }}>
            <p style={{ margin: "2px 0" }}>{printTicket?.ticket_id}</p>
            {printTicket?.ticket_id && (
              <Barcode value={printTicket?.ticket_id} width={1} height={30} displayValue={false} margin={0} />
            )}
          </div>
        </div>
        <Cut />
      </Printer>
    </>
  );
}

export default PrintPagePana;
